import React, { useState, useEffect } from 'react';
import { 
    BookOpen, Plus, Search, Edit2, Trash2, Video, Zap,
    FileText, CheckCircle, XCircle, Users, Settings, X, GripVertical, Save,
    Award, MessageSquare, ShieldAlert, AlertTriangle, Lock, UploadCloud, HelpCircle, ImagePlus
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import GhostwriterTab from '../components/GhostwriterTab';
import AutomationsTab from '../components/AutomationsTab';
import LiveChatMonitor from '../components/LiveChatMonitor';

// --- MOCK DATA: CERTIFICATES ---
const MOCK_CERTIFICATES = [
    { id: 'CERT-9091', user: 'Alex Hybrid', course: 'Advanced Risk Management', date: '2025-10-22' },
    { id: 'CERT-9092', user: 'Sarah Connor', course: 'Introduction to Forex Trading', date: '2025-10-24' },
];

// --- MOCK DATA: COMMUNITY CHANNELS & REPORTS ---
const MOCK_CHANNELS = [
    { id: 'CH-01', name: '#forex-beginners', activeUsers: 142, status: 'Active', mode: 'Public' },
    { id: 'CH-02', name: '#crypto-analysis', activeUsers: 89, status: 'Active', mode: 'Tier 2+' },
    { id: 'CH-03', name: '#risk-management-pro', activeUsers: 34, status: 'Active', mode: 'Tier 3+' },
    { id: 'CH-04', name: '#general-lounge', activeUsers: 312, status: 'Locked', mode: 'Public' },
];

const MOCK_REPORTS = [
    { id: 'REP-101', user: 'TraderSteve99', channel: '#forex-beginners', message: 'Guaranteed 1000% returns! Message me on Telegram!', reason: 'Spam / Scam Link', time: '10 mins ago', severity: 'High' },
    { id: 'REP-102', user: 'CryptoWhale', channel: '#crypto-analysis', message: 'You guys don\'t know what you are talking about, idiots.', reason: 'Harassment', time: '1 hour ago', severity: 'Medium' },
];

// --- MOCK DATA: COURSES ---
const INITIAL_COURSES = [
    {
        id: 'CRS-001',
        title: 'Introduction to Forex Trading',
        category: 'Forex',
        difficulty: 'Beginner',
        status: 'Published',
        enrolled: 1245,
        thumbnail: 'thumb_forex_101.jpg',
        description: 'Learn the absolute basics of currency pairs, pips, and leverage.',
        requiredTier: 'Tier 1', issuesCertificate: true, linkedChannel: '#forex-beginners',
        lessons: [
            { id: 'L1', title: 'What is Forex?', videoUrl: 'https://vimeo.com/111', content: 'Welcome to the Forex market...' },
            { id: 'L2', title: 'Understanding Pips & Lots', videoUrl: 'https://vimeo.com/222', content: 'A pip is the smallest price move...' },
            { id: 'L3', title: 'Leverage & Margin', videoUrl: 'https://vimeo.com/333', content: 'Leverage is a double-edged sword...' }
        ]
    },
    {
        id: 'CRS-002',
        title: 'Advanced Risk Management',
        category: 'Psychology & Risk',
        difficulty: 'Advanced',
        status: 'Published',
        enrolled: 890,
        thumbnail: 'thumb_risk_adv.jpg',
        description: 'Deep dive into position sizing, drawdown limits, and portfolio hedging.',
        requiredTier: 'Tier 3', issuesCertificate: true, linkedChannel: '#risk-management-pro',
        lessons: [
            { id: 'L1', title: 'The 1% Rule', videoUrl: 'https://vimeo.com/444', content: 'Never risk more than 1%...' }
        ]
    },
    {
        id: 'CRS-003',
        title: 'Crypto Market Analysis',
        category: 'Crypto',
        difficulty: 'Intermediate',
        status: 'Draft',
        enrolled: 0,
        thumbnail: '',
        description: 'On-chain analysis and reading order books for major cryptocurrencies.',
        requiredTier: 'Tier 2', issuesCertificate: false, linkedChannel: 'None',
        lessons: []
    }
];

const AcademyAdmin = () => {
    // Top Level State
    const [activeView, setActiveView] = useState('courses'); // 'courses' | 'certificates' | 'community'
    
    // Courses State
    const [courses, setCourses] = useState(INITIAL_COURSES);
    const [searchTerm, setSearchTerm] = useState('');
    const [certificates, setCertificates] = useState(MOCK_CERTIFICATES);
    
    // --- NEW: CATEGORY STATE ---
    const [categories, setCategories] = useState(['Forex', 'Crypto', 'Psychology & Risk', 'Platform Tutorials']);
    const [newCategory, setNewCategory] = useState('');
    
    // Community State
    const [reports, setReports] = useState([]);

    // Modal & Form State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // --- NEW: HUB MANAGEMENT STATE ---
    const [communities, setCommunities] = useState([]);
    const [availablePartners, setAvailablePartners] = useState([]);
    const [isHubModalOpen, setIsHubModalOpen] = useState(false);
    const [hubFormData, setHubFormData] = useState({
        id: null, name: '', lead_user_ids: [], 
        permissions: { nommia_education_access: false, nommia_webinar_access: false, custom_courses_enabled: true, enable_ghostwriter: true, target_defily_network: false },
        copy_whitelist: '',
        pamm_whitelist: '' 
    });

    const [partnerSearchTerm, setPartnerSearchTerm] = useState('');

    // --- FETCH LIVE DATA FROM SUPABASE ---
    useEffect(() => {
        fetchLiveCourses();
        fetchLiveCertificates(); 
        fetchHubData(); 
        fetchLiveReports(); // 🚨 NEW: Call the live moderation queue
    }, []);

    // 🚨 NEW: Fetch the live reports from Supabase
    const fetchLiveReports = async () => {
        try {
            const { data, error } = await supabase
                .from('community_reports')
                .select('*')
                .eq('status', 'Pending'); // Only pull reports that need review

            if (error) throw error;

            if (data) {
                // Map the database rows to match our UI
                const liveReports = data.map(dbReport => ({
                    id: dbReport.id,
                    user: dbReport.reported_user_id || 'Unknown User', // Will be replaced by real user info later
                    channel: dbReport.channel_name || 'General',
                    message: dbReport.message_content,
                    reason: dbReport.violation_type,
                    time: new Date(dbReport.created_at).toLocaleDateString(),
                    severity: dbReport.severity || 'Medium'
                }));
                setReports(liveReports);
            }
        } catch (error) {
            console.error("Error fetching live reports:", error.message);
        }
    };

    const fetchHubData = async () => {
        try {
            // 1. Fetch Hub configurations from Supabase
            const { data: comms } = await supabase.from('communities').select(`
                *,
                community_leads(lead_user_id, role)
            `);
            if (comms) setCommunities(comms);

            // 2. 🚨 SCALING UPGRADE: Fetch ONLY the currently assigned leads to populate the badges
            const allAssignedLeadIds = [];
            if (comms) {
                comms.forEach(c => {
                    c.community_leads?.forEach(l => {
                        if (l.lead_user_id) allAssignedLeadIds.push(l.lead_user_id);
                    });
                });
            }

            if (allAssignedLeadIds.length > 0) {
                const { data: leadUsers, error: usersError } = await supabase
                    .from('user_broker_accounts')
                    .select('id, user_id, name, email, first_name, last_name')
                    .in('id', allAssignedLeadIds);

                if (usersError) throw usersError;

                if (leadUsers) {
                    const masterUsers = leadUsers.map(u => ({
                        id: u.id, 
                        // Bulletproof name rendering
                        name: u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Unnamed Client',
                        email: u.email || u.user_id 
                    }));
                    setAvailablePartners(masterUsers);
                }
            } else {
                setAvailablePartners([]);
            }
        } catch (error) {
            console.error("Error fetching hub data:", error);
        }
    };

    // 🚨 NEW: LIVE SERVER-SIDE SEARCH FOR PARTNERS
    useEffect(() => {
        // Wait until they type at least 2 characters to avoid spamming the database
        if (!partnerSearchTerm || partnerSearchTerm.length < 2) return;

        const searchTimer = setTimeout(async () => {
            try {
                const searchStr = partnerSearchTerm.toLowerCase();
                const { data, error } = await supabase
                    .from('user_broker_accounts')
                    .select('id, user_id, name, email, first_name, last_name')
                    .or(`name.ilike.%${searchStr}%,email.ilike.%${searchStr}%,user_id.ilike.%${searchStr}%`)
                    .limit(20); // Only bring back the top 20 closest matches
                
                if (error) throw error;
                
                if (data) {
                    const mapped = data.map(u => ({
                        id: u.id,
                        name: u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Unnamed Client',
                        email: u.email || u.user_id
                    }));
                    
                    // Merge new search results with existing assigned partners so we don't wipe out the badges!
                    setAvailablePartners(prev => {
                        const existingIds = new Set(prev.map(p => p.id));
                        const newPartners = mapped.filter(p => !existingIds.has(p.id));
                        return [...prev, ...newPartners];
                    });
                }
            } catch (err) {
                console.error("Partner live search failed:", err);
            }
        }, 400); // 400ms debounce (waits for them to stop typing)

        return () => clearTimeout(searchTimer);
    }, [partnerSearchTerm]);

    const [formData, setFormData] = useState({
        title: '', category: 'Forex', difficulty: 'Beginner', 
        status: 'Draft', thumbnail: '', description: '',
        requiredTier: 'Tier 1', issuesCertificate: false, linkedChannel: 'None',
        lessons: []
    });

    // --- FETCH LIVE DATA FROM SUPABASE ---
    useEffect(() => {
        fetchLiveCourses();
        fetchLiveCertificates(); // 🚨 Added this so certificates load on page open
    }, []);

    const fetchLiveCourses = async () => {
        try {
            const { data, error } = await supabase
                .from('academy_courses')
                .select('*');

            if (error) throw error;

            if (data && data.length > 0) {
                // Map the database rows back to match our frontend UI structure
                const liveCourses = data.map(dbCourse => ({
                    id: dbCourse.id,
                    title: dbCourse.title,
                    description: dbCourse.description,
                    requiredTier: dbCourse.required_tier,
                    issuesCertificate: dbCourse.has_certificate,
                    linkedChannel: dbCourse.community_channel,
                    status: dbCourse.status,
                    quiz: dbCourse.quiz_data || { passingScore: 80, questions: [] },
                    resources: dbCourse.resources || [],
                    lessons: dbCourse.lessons || [],
                    
                    // Now mapping directly from your newly updated Supabase columns!
                    category: dbCourse.category || 'Uncategorized', 
                    difficulty: dbCourse.difficulty || 'Beginner',
                    thumbnail: dbCourse.thumbnail || '',
                    enrolled: 0 
                }));
                
                // Overwrite the mock INITIAL_COURSES with your real database courses!
                setCourses(liveCourses);
            }
        } catch (error) {
            console.error("Error fetching live courses:", error.message);
        }
    };

    const fetchLiveCertificates = async () => {
        try {
            const { data, error } = await supabase
                .from('issued_certificates')
                .select('*')
                .order('issued_at', { ascending: false });

            if (error) throw error;

            if (data) {
                // Map it to match your UI format
                const liveCerts = data.map(dbCert => ({
                    id: dbCert.certificate_id,
                    user: dbCert.user_name,
                    course: dbCert.course_title,
                    date: new Date(dbCert.issued_at).toLocaleDateString()
                }));
                
                // Overwrite the MOCK_CERTIFICATES with real data
                setCertificates(liveCerts); 
            }
        } catch (error) {
            console.error("Error fetching live certificates:", error.message);
        }
    };

// --- COURSES HANDLERS ---
    const handleOpenModal = (course = null) => {
        if (course) {
            setEditingId(course.id);
            // We add the defaults before spreading ...course so old mock data doesn't break
            setFormData({
                requiredTier: 'Tier 1', 
                issuesCertificate: false, 
                linkedChannel: 'None', 
                resources: [], 
                quiz: { passingScore: 80, questions: [] },
                ...course
            });
        } else {
            setEditingId(null);
            setFormData({
                title: '', category: 'Forex', difficulty: 'Beginner', 
                status: 'Draft', thumbnail: '', description: '', 
                requiredTier: 'Tier 1', issuesCertificate: false, linkedChannel: 'None',
                lessons: [{ id: Date.now().toString(), title: 'Lesson 1', videoUrl: '', content: '', quiz: { passingScore: 80, questions: [] }, resources: [] }],
                resources: []
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingId(null);
    };

const handleSaveCourse = async () => {
        if (!formData.title) return alert("Course Title is required.");

        try {
            // Map frontend state to your exact Supabase PostgreSQL schema
            const coursePayload = {
                title: formData.title,
                description: formData.description,
                required_tier: formData.requiredTier,
                video_url: formData.lessons?.length > 0 ? formData.lessons[0].videoUrl : '',
                has_certificate: formData.issuesCertificate,
                community_channel: formData.linkedChannel,
                status: formData.status,
                quiz_data: formData.quiz || null,
                resources: formData.resources || null,
                lessons: formData.lessons || [],
                category: formData.category,       
                difficulty: formData.difficulty,   
                thumbnail: formData.thumbnail,      
                community_id: formData.community_id === 'Global' ? null : formData.community_id
            };

            if (editingId) {
                // UPDATE EXISTING COURSE
                const { error } = await supabase
                    .from('academy_courses')
                    .update(coursePayload)
                    .eq('id', editingId);

                if (error) throw error;
                
                // Update local UI state
                setCourses(courses.map(c => c.id === editingId ? { ...c, ...formData } : c));
                alert("Course successfully updated in Supabase!");

            } else {
                // INSERT NEW COURSE
                const { data, error } = await supabase
                    .from('academy_courses')
                    .insert([coursePayload])
                    .select(); // Select returns the newly generated DB row

                if (error) throw error;

                // Add the new row (including the real DB-generated ID) to the UI
                const newCourse = data ? { ...formData, id: data[0].id, enrolled: 0 } : { ...formData, id: Date.now().toString(), enrolled: 0 };
                setCourses([newCourse, ...courses]);
                alert("Course successfully published to Supabase!");
            }
            
            handleCloseModal();

        } catch (error) {
            console.error("Supabase Error:", error);
            alert(`Failed to save course: ${error.message}`);
        }
    };

    const handleDeleteCourse = (id) => {
        if (window.confirm("Are you sure you want to delete this course? This will remove it from the Trading Platform.")) {
            setCourses(courses.filter(c => c.id !== id));
        }
    };

    // --- NEW: HUB HANDLERS ---
    const handleOpenHubModal = (hub = null) => {
        if (hub) {
            // 🚨 Extract ALL existing lead IDs into an array
            const existingLeadIds = hub.community_leads ? hub.community_leads.map(l => l.lead_user_id) : [];
            
            // 🚨 THE FIX: Bulletproof parsing for Supabase data types
            // Checks if it's an array before joining, otherwise uses the raw string, otherwise empty.
            const safeCopyWhitelist = Array.isArray(hub.copy_whitelist) ? hub.copy_whitelist.join(', ') : (hub.copy_whitelist || '');
            const safePammWhitelist = Array.isArray(hub.pamm_whitelist) ? hub.pamm_whitelist.join(', ') : (hub.pamm_whitelist || '');

            setHubFormData({
                id: hub.id, name: hub.name, 
                lead_user_ids: existingLeadIds,
                permissions: hub.permissions || { nommia_education_access: false, nommia_webinar_access: false, custom_courses_enabled: true, enable_ghostwriter: true },
                copy_whitelist: safeCopyWhitelist,
                pamm_whitelist: safePammWhitelist
            });
        } else {
            setHubFormData({ id: null, name: '', lead_user_ids: [], permissions: { nommia_education_access: false, nommia_webinar_access: false, custom_courses_enabled: true, enable_ghostwriter: true }, copy_whitelist: '', pamm_whitelist: '' });
        }
        setIsHubModalOpen(true);
    };

    const handleSaveHub = async () => {
        if (!hubFormData.name) return alert("Hub Name is required");
        try {
            let currentCommunityId = hubFormData.id;

            // 1. Save or Create the Hub
            // 🚨 Convert comma strings back to clean arrays
            const copyArray = hubFormData.copy_whitelist ? hubFormData.copy_whitelist.split(',').map(id => id.trim()).filter(Boolean) : [];
            const pammArray = hubFormData.pamm_whitelist ? hubFormData.pamm_whitelist.split(',').map(id => id.trim()).filter(Boolean) : [];
            
            const payload = { 
                name: hubFormData.name, 
                permissions: hubFormData.permissions,
                copy_whitelist: copyArray, // 🚨 ADDED
                pamm_whitelist: pammArray  // 🚨 ADDED
            };

            if (currentCommunityId) {
                await supabase.from('communities').update(payload).eq('id', currentCommunityId);
            } else {
                const { data: newHub, error: hubError } = await supabase.from('communities').insert([payload]).select().single();
                if (hubError) throw hubError;
                currentCommunityId = newHub.id;
            }

            // 2. Multi-Lead Assignment (🚨 UPGRADED)
            // ALWAYS wipe old leads for this hub to ensure a clean sync
            await supabase.from('community_leads').delete().eq('community_id', currentCommunityId);
            
            // Insert the new array of leads if any are selected
            if (hubFormData.lead_user_ids && hubFormData.lead_user_ids.length > 0) {
                const leadInserts = hubFormData.lead_user_ids.map(userId => ({
                    community_id: currentCommunityId, 
                    lead_user_id: userId
                }));
                const { error: leadError } = await supabase.from('community_leads').insert(leadInserts);
                if (leadError) throw leadError;
            }

            fetchHubData(); // Refresh list
            setIsHubModalOpen(false);
            alert("Community Hub saved successfully!");
        } catch (error) {
            console.error("Hub save error:", error);
            alert("Failed to save Hub. Check console.");
        }
    };

    // --- CATEGORY HANDLER ---
    const handleAddCategory = () => {
        const trimmed = newCategory.trim();
        if (trimmed && !categories.includes(trimmed)) {
            setCategories([...categories, trimmed]);
            // Automatically select the newly created category in the form
            setFormData({ ...formData, category: trimmed }); 
            setNewCategory(''); // Clear the input
        }
    };

    // --- MULTI-LESSON HANDLERS ---
    const handleAddLesson = () => {
        setFormData({
            ...formData,
            lessons: [...(formData.lessons || []), { id: Date.now().toString(), title: `Lesson ${(formData.lessons || []).length + 1}`, videoUrl: '', content: '', quiz: { passingScore: 80, questions: [] }, resources: [] }]
        });
    };

    const handleRemoveLesson = (lessonId) => {
        setFormData({
            ...formData,
            lessons: formData.lessons.filter(l => l.id !== lessonId)
        });
    };

    const handleLessonChange = (lessonId, field, value) => {
        const updatedLessons = formData.lessons.map(lesson => 
            lesson.id === lessonId ? { ...lesson, [field]: value } : lesson
        );
        setFormData({ ...formData, lessons: updatedLessons });
    };

// --- RESOURCES HANDLERS (NOW WITH REAL UPLOADS) ---
    
    // Helper function to handle the actual Supabase Storage push
    const uploadToSupabaseStorage = async (file) => {
        // Sanitize the original filename (keeps original name, replaces spaces with hyphens)
        const safeFileName = file.name.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9.-]/g, '');
        const filePath = `course-materials/${safeFileName}`;

        const { error: uploadError } = await supabase.storage
            .from('academy_files')
            .upload(filePath, file, {
                cacheControl: '3600',
                upsert: true // 🚨 Allows overwriting the exact same file name!
            });

        if (uploadError) throw uploadError;

        // Get the public URL to save to the database
        const { data } = supabase.storage.from('academy_files').getPublicUrl(filePath);
        
        return {
            id: safeFileName,
            name: file.name,
            size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
            url: data.publicUrl // The real download link!
        };
    };

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        
        try {
            // Using Promise.all to upload multiple files simultaneously
            const uploadedResources = await Promise.all(files.map(f => uploadToSupabaseStorage(f)));
            setFormData(prev => ({ ...prev, resources: [...(prev.resources || []), ...uploadedResources] }));
        } catch (err) {
            console.error("Upload failed:", err);
            alert("Failed to upload files to Supabase Storage.");
        }
    };

    const handleRemoveResource = (id) => {
        setFormData({ ...formData, resources: (formData.resources || []).filter(r => r.id !== id) });
    };

    // --- LESSON RESOURCES HANDLERS ---
    const handleLessonFileUpload = async (lessonId, e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        
        try {
            const uploadedResources = await Promise.all(files.map(f => uploadToSupabaseStorage(f)));
            
            setFormData(prev => ({
                ...prev,
                lessons: prev.lessons.map(l => 
                    l.id === lessonId ? { ...l, resources: [...(l.resources || []), ...uploadedResources] } : l
                )
            }));
        } catch (err) {
            console.error("Lesson Upload failed:", err);
            alert("Failed to upload lesson files to Supabase Storage.");
        }
    };

    const handleRemoveLessonResource = (lessonId, resourceId) => {
        setFormData(prev => ({
            ...prev,
            lessons: prev.lessons.map(l => 
                l.id === lessonId ? { ...l, resources: (l.resources || []).filter(r => r.id !== resourceId) } : l
            )
        }));
    };

    // --- QUIZ HANDLERS (LESSON-SPECIFIC) ---
    const updateLessonQuiz = (lessonId, updater) => {
        setFormData(prev => ({
            ...prev,
            lessons: prev.lessons.map(l => l.id === lessonId ? { ...l, quiz: updater(l.quiz || { passingScore: 80, questions: [] }) } : l)
        }));
    };

    const handleAddQuestion = (lessonId) => {
        const newQ = { id: Date.now(), type: 'multiple_choice', questionText: '', options: ['', '', '', ''], correctIndex: 0, imageUrl: '' };
        updateLessonQuiz(lessonId, quiz => ({ ...quiz, questions: [...quiz.questions, newQ] }));
    };

    const handleRemoveQuestion = (lessonId, qId) => {
        updateLessonQuiz(lessonId, quiz => ({ ...quiz, questions: quiz.questions.filter(q => q.id !== qId) }));
    };

    const handleQuestionChange = (lessonId, qId, text) => {
        updateLessonQuiz(lessonId, quiz => ({ ...quiz, questions: quiz.questions.map(q => q.id === qId ? { ...q, questionText: text } : q) }));
    };

    const handleQuestionTypeChange = (lessonId, qId, type) => {
        updateLessonQuiz(lessonId, quiz => ({
            ...quiz, 
            questions: quiz.questions.map(q => {
                if (q.id === qId) {
                    if (type === 'true_false') return { ...q, type, options: ['True', 'False'], correctIndex: 0 };
                    return { ...q, type, options: ['', '', '', ''], correctIndex: 0 };
                }
                return q;
            })
        }));
    };

    const handleQuestionImageUpload = (lessonId, qId, e) => {
        const file = e.target.files[0];
        if (!file) return;
        const fakeUrl = URL.createObjectURL(file); 
        updateLessonQuiz(lessonId, quiz => ({ ...quiz, questions: quiz.questions.map(q => q.id === qId ? { ...q, imageUrl: fakeUrl } : q) }));
    };

    const handleRemoveQuestionImage = (lessonId, qId) => {
        updateLessonQuiz(lessonId, quiz => ({ ...quiz, questions: quiz.questions.map(q => q.id === qId ? { ...q, imageUrl: '' } : q) }));
    };

    const handleOptionChange = (lessonId, qId, optIndex, text) => {
        updateLessonQuiz(lessonId, quiz => ({
            ...quiz, 
            questions: quiz.questions.map(q => {
                if (q.id === qId) {
                    const newOpts = [...q.options];
                    newOpts[optIndex] = text;
                    return { ...q, options: newOpts };
                }
                return q;
            })
        }));
    };

    const handleCorrectAnswer = (lessonId, qId, optIndex) => {
        updateLessonQuiz(lessonId, quiz => ({ ...quiz, questions: quiz.questions.map(q => q.id === qId ? { ...q, correctIndex: optIndex } : q) }));
    };

    const handlePassingScore = (lessonId, score) => {
        updateLessonQuiz(lessonId, quiz => ({ ...quiz, passingScore: parseInt(score, 10) || 80 }));
    };

    // --- COMMUNITY HANDLERS ---
    const handleResolveReport = async (id, action) => {
        // 1. Instantly remove it from the screen so it feels fast
        const previousReports = [...reports];
        setReports(reports.filter(r => r.id !== id));

        try {
            // 2. Tell Supabase to actually update the row
            const { data, error } = await supabase
                .from('community_reports')
                .update({ status: 'Resolved' })
                .eq('id', id)
                .select();

            if (error) throw error;

            // Optional: If you eventually build the automated warning emails, you would trigger them here!
            alert(`✅ ${action} applied to report ${id}.`);

        } catch (error) {
            console.error("Failed to resolve report:", error);
            // 3. If Supabase fails (e.g. RLS error), put the report back on the screen
            setReports(previousReports);
            alert("❌ Failed to update the database. Check console for errors.");
        }
    };

    // Filter courses for search
    const filteredCourses = courses.filter(c => 
        c.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        c.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-6 md:p-12 pb-24 max-w-7xl mx-auto min-h-screen animate-fade-in">
            
            {/* HEADER */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                        <BookOpen className="text-amber-500" size={32} /> Academy & Community
                    </h1>
                    <p className="text-slate-400">Manage trading courses, certificates, and community chat moderation.</p>
                </div>
                {activeView === 'courses' && (
                    <button 
                        onClick={() => handleOpenModal()}
                        className="bg-amber-600 hover:bg-amber-500 text-white font-bold py-2.5 px-6 rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-amber-900/20"
                    >
                        <Plus size={18} /> Create New Course
                    </button>
                )}
            </div>

            {/* VIEW SWITCHER TABS (This is where Community is accessed) */}
            <div className="flex gap-4 border-b border-slate-800 mb-6 pb-2">
                <button 
                    onClick={() => setActiveView('courses')}
                    className={`pb-2 text-sm font-bold capitalize transition-colors border-b-2 flex items-center gap-2 ${activeView === 'courses' ? 'border-amber-500 text-amber-500' : 'border-transparent text-slate-500 hover:text-white'}`}
                >
                    <Video size={16}/> Course Library
                </button>
                <button 
                    onClick={() => setActiveView('certificates')}
                    className={`pb-2 text-sm font-bold capitalize transition-colors border-b-2 flex items-center gap-2 ${activeView === 'certificates' ? 'border-amber-500 text-amber-500' : 'border-transparent text-slate-500 hover:text-white'}`}
                >
                    <Award size={16}/> Issued Certificates
                </button>
                <button 
                    onClick={() => setActiveView('community')}
                    className={`pb-2 text-sm font-bold capitalize transition-colors border-b-2 flex items-center gap-2 ${activeView === 'community' ? 'border-amber-500 text-amber-500' : 'border-transparent text-slate-500 hover:text-white'}`}
                >
                    <MessageSquare size={16}/> Community Mod
                    {reports.length > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 rounded-full">{reports.length}</span>}
                </button>
                <button 
                    onClick={() => setActiveView('ghostwriter')}
                    className={`pb-2 text-sm font-bold capitalize transition-colors border-b-2 flex items-center gap-2 ${activeView === 'ghostwriter' ? 'border-amber-500 text-amber-500' : 'border-transparent text-slate-500 hover:text-white'}`}
                >
                    <Edit2 size={16}/> Ghostwriter
                </button>
                <button 
                    onClick={() => setActiveView('automations')}
                    className={`pb-2 text-sm font-bold capitalize transition-colors border-b-2 flex items-center gap-2 ${activeView === 'automations' ? 'border-amber-500 text-amber-500' : 'border-transparent text-slate-500 hover:text-white'}`}
                >
                    <Zap size={16}/> Automations
                </button>
            </div> 

            {/* --- TAB 1: COURSES VIEW --- */}
            {activeView === 'courses' && (
                <div className="animate-fade-in space-y-8">
                    {/* QUICK STATS */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-slate-900 border border-slate-800 p-5 rounded-xl flex items-center gap-4">
                            <div className="p-3 bg-blue-500/10 text-blue-400 rounded-lg"><BookOpen size={24} /></div>
                            <div><p className="text-xs text-slate-500 font-bold uppercase">Total Courses</p><p className="text-2xl font-bold text-white">{courses.length}</p></div>
                        </div>
                        <div className="bg-slate-900 border border-slate-800 p-5 rounded-xl flex items-center gap-4">
                            <div className="p-3 bg-emerald-500/10 text-emerald-400 rounded-lg"><CheckCircle size={24} /></div>
                            <div><p className="text-xs text-slate-500 font-bold uppercase">Published</p><p className="text-2xl font-bold text-white">{courses.filter(c => c.status === 'Published').length}</p></div>
                        </div>
                        <div className="bg-slate-900 border border-slate-800 p-5 rounded-xl flex items-center gap-4">
                            <div className="p-3 bg-slate-800 text-slate-400 rounded-lg"><FileText size={24} /></div>
                            <div><p className="text-xs text-slate-500 font-bold uppercase">Drafts</p><p className="text-2xl font-bold text-white">{courses.filter(c => c.status === 'Draft').length}</p></div>
                        </div>
                        <div className="bg-slate-900 border border-slate-800 p-5 rounded-xl flex items-center gap-4">
                            <div className="p-3 bg-purple-500/10 text-purple-400 rounded-lg"><Users size={24} /></div>
                            <div><p className="text-xs text-slate-500 font-bold uppercase">Total Enrolled</p><p className="text-2xl font-bold text-white">{courses.reduce((acc, curr) => acc + (curr.enrolled || 0), 0)}</p></div>
                        </div>
                    </div>

                    {/* MAIN DASHBOARD TABLE */}
                    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl">
                        <div className="p-4 border-b border-slate-800 bg-slate-950/50 flex justify-between items-center">
                            <div className="relative w-72">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                <input 
                                    type="text" 
                                    placeholder="Search courses..." 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                                />
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm whitespace-nowrap">
                                <thead className="bg-slate-950 text-slate-400 uppercase font-bold text-xs border-b border-slate-800">
                                    <tr>
                                        <th className="px-6 py-4">Course Details</th>
                                        <th className="px-6 py-4">Category</th>
                                        <th className="px-6 py-4">Size</th>
                                        <th className="px-6 py-4 text-center">Enrolled</th>
                                        <th className="px-6 py-4 text-center">Status</th>
                                        <th className="px-6 py-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800 text-slate-300">
                                    {filteredCourses.length > 0 ? filteredCourses.map((course) => (
                                        <tr key={course.id} className="hover:bg-slate-800/40 transition-colors group">
                                            <td className="px-6 py-4">
                                                <p className="font-bold text-white text-base">{course.title}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <p className="text-xs text-slate-500 font-mono">{course.id}</p>
                                                    {course.requiredTier && <span className="text-[10px] bg-indigo-500/10 text-indigo-400 px-1.5 py-0.5 rounded font-bold border border-indigo-500/20">{course.requiredTier}</span>}
                                                    {course.issuesCertificate && <Award size={12} className="text-amber-500" title="Issues Certificate"/>}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="bg-slate-800 px-3 py-1 rounded-full text-xs font-medium border border-slate-700">
                                                    {course.category}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-xs text-slate-400 flex items-center gap-1">
                                                    <Video size={14} className="text-blue-400"/> {(course.lessons || []).length} Modules
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center font-mono font-medium">
                                                {(course.enrolled || 0).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border ${
                                                    course.status === 'Published' 
                                                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                                                    : 'bg-slate-800 text-slate-400 border-slate-700'
                                                }`}>
                                                    {course.status === 'Published' ? <CheckCircle size={12} /> : <Settings size={12} />}
                                                    {course.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end gap-2 opacity-50 group-hover:opacity-100 transition-opacity">
                                                    <button 
                                                        onClick={() => handleOpenModal(course)}
                                                        className="p-2 bg-slate-800 hover:bg-amber-600 hover:text-white text-slate-400 rounded-lg transition-colors border border-slate-700 hover:border-amber-500"
                                                        title="Edit Course"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteCourse(course.id)}
                                                        className="p-2 bg-slate-800 hover:bg-red-600 hover:text-white text-slate-400 rounded-lg transition-colors border border-slate-700 hover:border-red-500"
                                                        title="Delete Course"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                                                <BookOpen size={48} className="mx-auto mb-4 opacity-20" />
                                                <p>No courses found matching your search.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* --- TAB 2: CERTIFICATES VIEW --- */}
            {activeView === 'certificates' && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl animate-fade-in">
                    <div className="p-4 border-b border-slate-800 bg-slate-950/50">
                        <h3 className="font-bold text-white flex items-center gap-2"><Award className="text-amber-500"/> Certificate Ledger</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-slate-950 text-slate-400 uppercase font-bold text-xs border-b border-slate-800">
                                <tr>
                                    <th className="px-6 py-4">Certificate ID</th>
                                    <th className="px-6 py-4">User Name</th>
                                    <th className="px-6 py-4">Completed Course</th>
                                    <th className="px-6 py-4 text-right">Date Issued</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800 text-slate-300">
                                {MOCK_CERTIFICATES.map((cert) => (
                                    <tr key={cert.id} className="hover:bg-slate-800/40 transition-colors">
                                        <td className="px-6 py-4 font-mono text-amber-500">{cert.id}</td>
                                        <td className="px-6 py-4 font-bold text-white">{cert.user}</td>
                                        <td className="px-6 py-4">{cert.course}</td>
                                        <td className="px-6 py-4 text-right">{cert.date}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* --- TAB 3: MASTER HUB DASHBOARD --- */}
            {activeView === 'community' && (
                <div className="animate-fade-in space-y-8">
                    <div className="flex justify-between items-center bg-slate-900 border border-slate-800 p-6 rounded-2xl">
                        <div>
                            <h2 className="text-xl font-bold text-white flex items-center gap-2"><Users className="text-blue-500"/> Partner Community White-Labels</h2>
                            <p className="text-sm text-slate-400 mt-1">Manage white-label communities, permissions, and assigned leads.</p>
                        </div>
                        <button onClick={() => handleOpenHubModal()} className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 px-6 rounded-lg transition-colors flex items-center gap-2">
                            <Plus size={18} /> Create New Hub
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {communities.length > 0 ? communities.map(hub => (
                            <div key={hub.id} className="bg-slate-900 border border-slate-800 rounded-xl p-6 relative group hover:border-blue-500/50 transition-colors">
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="font-bold text-white text-lg">{hub.name}</h3>
                                    <button onClick={() => handleOpenHubModal(hub)} className="text-slate-500 hover:text-blue-400 transition-colors"><Edit2 size={16}/></button>
                                </div>
                                
                                <div className="space-y-2 mb-6">
                                    <p className="text-xs text-slate-500 font-bold uppercase">Assigned Leads</p>
                                    {hub.community_leads?.length > 0 ? (
                                        <div className="flex gap-2 flex-wrap">
                                            {hub.community_leads.map((lead, i) => {
                                                const partner = availablePartners.find(p => p.id === lead.lead_user_id);
                                                return <span key={i} className="bg-blue-500/10 text-blue-400 text-xs px-2 py-1 rounded border border-blue-500/20">{partner?.name || 'Lead ID'}</span>
                                            })}
                                        </div>
                                    ) : <span className="text-xs text-slate-600 italic">No leads assigned</span>}
                                </div>

                                <div className="space-y-2 border-t border-slate-800 pt-4">
                                    <p className="text-xs text-slate-500 font-bold uppercase mb-2">Active Perks</p>
                                    <div className="flex flex-col gap-1.5">
                                        <span className={`text-xs flex items-center gap-2 ${hub.permissions?.nommia_education_access ? 'text-emerald-400' : 'text-slate-600'}`}><CheckCircle size={12}/> Global Education</span>
                                        <span className={`text-xs flex items-center gap-2 ${hub.permissions?.custom_courses_enabled ? 'text-emerald-400' : 'text-slate-600'}`}><CheckCircle size={12}/> Private Courses</span>
                                        <span className={`text-xs flex items-center gap-2 ${hub.permissions?.enable_ghostwriter ? 'text-emerald-400' : 'text-slate-600'}`}><CheckCircle size={12}/> Ghostwriter Active</span>
                                    </div>
                                </div>
                            </div>
                        )) : (
                            <div className="col-span-full text-center p-12 bg-slate-900/50 border border-dashed border-slate-800 rounded-xl text-slate-500">
                                <Users size={48} className="mx-auto mb-4 opacity-20" />
                                <p className="font-bold">No Hubs Configured</p>
                                <p className="text-sm mt-1">Click "Create New Hub" to launch your first white-label community.</p>
                            </div>
                        )}
                    </div>

                    {/* RESTORED: MODERATION QUEUE */}
                    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl mt-8">
                        <div className="p-4 border-b border-slate-800 bg-slate-950/50 flex justify-between items-center">
                            <h3 className="font-bold text-white flex items-center gap-2"><ShieldAlert className="text-red-500"/> Global Moderation Queue</h3>
                            <span className="bg-red-500/10 text-red-400 text-xs px-2 py-1 rounded font-bold">{reports.length} Needs Review</span>
                        </div>
                        
                        <div className="p-4 space-y-4">
                            {reports.length > 0 ? reports.map(report => (
                                <div key={report.id} className="bg-slate-950 border border-slate-800 rounded-lg p-4 relative group">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center font-bold text-xs text-slate-400">
                                                {report.user.charAt(0)}
                                            </div>
                                            <div>
                                                <p className="font-bold text-white text-sm">{report.user}</p>
                                                <p className="text-[10px] text-slate-500">in <span className="text-blue-400">{report.channel}</span> • {report.time}</p>
                                            </div>
                                        </div>
                                        <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded border ${report.severity === 'High' ? 'bg-red-500/10 text-red-500 border-red-500/20' : 'bg-amber-500/10 text-amber-500 border-amber-500/20'}`}>
                                            Flag: {report.reason}
                                        </span>
                                    </div>
                                    <div className="bg-slate-900 border border-slate-800 p-3 rounded text-sm text-slate-300 italic mb-4">
                                        "{report.message}"
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleResolveReport(report.id, 'Message Deleted & User Warned')} className="flex-1 bg-red-900/20 hover:bg-red-600 text-red-400 hover:text-white border border-red-500/30 py-2 rounded text-xs font-bold transition-colors flex items-center justify-center gap-2">
                                            <Trash2 size={14}/> Delete & Warn
                                        </button>
                                        <button onClick={() => handleResolveReport(report.id, 'User Banned')} className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded text-xs font-bold transition-colors">
                                            Ban User
                                        </button>
                                        <button onClick={() => handleResolveReport(report.id, 'Report Dismissed')} className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded text-xs font-bold transition-colors">
                                            Dismiss
                                        </button>
                                    </div>
                                </div>
                            )) : (
                                <div className="text-center p-12 text-slate-500">
                                    <CheckCircle size={48} className="mx-auto mb-4 opacity-20 text-emerald-500" />
                                    <p className="font-bold">Queue is empty</p>
                                    <p className="text-sm mt-1">All reports have been handled.</p>
                                </div>
                            )}
                        </div>
                    </div>
                    <LiveChatMonitor communities={communities} />
                </div>
            )}

            {/* --- TAB 4: GHOSTWRITER VIEW --- */}
            {activeView === 'ghostwriter' && (
                <div className="animate-fade-in">
                    <GhostwriterTab communities={communities} availablePartners={availablePartners} />
                </div>
            )}

            {/* --- TAB 5: AUTOMATIONS VIEW --- */}
            {activeView === 'automations' && (
                <div className="animate-fade-in">
                    <AutomationsTab />
                </div>
            )}

            {/* --- COURSE BUILDER MODAL --- */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-950/80 backdrop-blur-sm overflow-y-auto animate-fade-in">
                    <div className="bg-slate-900 border border-slate-700 w-full max-w-5xl rounded-2xl shadow-2xl my-auto max-h-[90vh] flex flex-col">
                        
                        {/* Modal Header */}
                        <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-900 rounded-t-2xl shrink-0">
                            <div>
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    <Settings className="text-amber-500" /> {editingId ? 'Edit Course' : 'Course Builder'}
                                </h3>
                                <p className="text-xs text-slate-400 mt-1">Manage course metadata and multi-lesson modules.</p>
                            </div>
                            <button onClick={handleCloseModal} className="text-slate-500 hover:text-white bg-slate-800 hover:bg-slate-700 p-2 rounded-lg transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        {/* Modal Body (Scrollable Form) */}
                        <div className="p-6 space-y-8 overflow-y-auto custom-scrollbar flex-1">
                            
                            {/* SECTION 1: Course Metadata */}
                            <div className="space-y-6 bg-slate-950/50 p-6 rounded-xl border border-slate-800">
                                <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2 border-b border-slate-800 pb-3">
                                    1. Course Information
                                </h4>
                                
                                {/* Row 1: Title & Hub Assignment */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Course Title</label>
                                        <input type="text" className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g. Command Your Cashflow" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2 text-purple-400">Assign to Hub (White-Label)</label>
                                        <select className="w-full bg-slate-900 border border-purple-500/30 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-purple-500" value={formData.community_id || 'Global'} onChange={e => setFormData({...formData, community_id: e.target.value})}>
                                            <option value="Global">🌐 Global (All Nommia Users)</option>
                                            {communities.map(hub => <option key={hub.id} value={hub.id}>🔒 {hub.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* Row 2: Category & Difficulty */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Category</label>
                                        <select className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-amber-500 mb-2" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}>
                                            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                        </select>
                                        <div className="flex gap-2">
                                            <input type="text" placeholder="Or create new..." value={newCategory} onChange={e => setNewCategory(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-amber-500" />
                                            <button onClick={handleAddCategory} className="bg-slate-800 hover:bg-amber-600 text-white px-3 rounded-lg text-xs font-bold transition-colors border border-slate-700 hover:border-amber-500">Add</button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Difficulty</label>
                                        <select className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-amber-500" value={formData.difficulty} onChange={e => setFormData({...formData, difficulty: e.target.value})}>
                                            <option>Beginner</option>
                                            <option>Intermediate</option>
                                            <option>Advanced</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Row 3: Gating & Community (RESTORED) */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4 bg-slate-900 border border-slate-800 rounded-xl">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Required Membership</label>
                                        <select className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-amber-500" value={formData.requiredTier} onChange={e => setFormData({...formData, requiredTier: e.target.value})}>
                                            <option value="Tier 1">Tier 1 (Trader)</option>
                                            <option value="Tier 2">Tier 2 (Pro)</option>
                                            <option value="Tier 3">Tier 3 (Elite)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2 flex items-center gap-1"><MessageSquare size={12}/> Linked Chat Channel</label>
                                        <select className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-amber-500" value={formData.linkedChannel} onChange={e => setFormData({...formData, linkedChannel: e.target.value})}>
                                            <option>None</option>
                                            <option>#forex-beginners</option>
                                            <option>#crypto-analysis</option>
                                            <option>#risk-management-pro</option>
                                        </select>
                                    </div>
                                    <div className="flex flex-col justify-center items-center bg-slate-950 rounded-lg border border-slate-700 p-2">
                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2 flex items-center gap-1"><Award size={12}/> Issue Certificate?</label>
                                        <button onClick={() => setFormData({...formData, issuesCertificate: !formData.issuesCertificate})} className={`w-12 h-6 rounded-full p-1 transition-colors relative flex items-center ${formData.issuesCertificate ? 'bg-amber-500' : 'bg-slate-700'}`}>
                                            <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform absolute ${formData.issuesCertificate ? 'translate-x-6' : 'translate-x-0'}`} />
                                        </button>
                                    </div>
                                </div>

                                {/* Row 4: Thumbnail & Description (RESTORED) */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Thumbnail URL</label>
                                        <input type="text" className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-amber-500" placeholder="Image URL for course card..." value={formData.thumbnail} onChange={e => setFormData({...formData, thumbnail: e.target.value})} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Short Description</label>
                                        <textarea className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white h-[46px] resize-none focus:outline-none focus:border-amber-500" placeholder="Brief overview visible on the course card..." value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}></textarea>
                                    </div>
                                </div>
                            </div>

                            {/* SECTION 2: Lesson Builder (The Array) */}
                            <div className="space-y-4">
                                <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                                    <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                                        2. Curriculum / Lessons ({(formData.lessons || []).length})
                                    </h4>
                                    <button 
                                        onClick={handleAddLesson}
                                        className="bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white border border-blue-500/30 text-xs font-bold px-3 py-1.5 rounded transition-colors flex items-center gap-1"
                                    >
                                        <Plus size={14} /> Add Lesson
                                    </button>
                                </div>

                                {/* Dynamic Lesson List */}
                                {(formData.lessons || []).length === 0 ? (
                                    <div className="text-center p-8 bg-slate-950 border border-dashed border-slate-800 rounded-xl text-slate-500">
                                        <Video size={32} className="mx-auto mb-2 opacity-50"/>
                                        <p>No lessons added yet. Click "Add Lesson" to start building the curriculum.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {formData.lessons.map((lesson, index) => (
                                            <div key={lesson.id} className="bg-slate-800/30 border border-slate-700 rounded-xl p-4 flex gap-4 animate-fade-in relative group hover:border-slate-500 transition-colors">
                                                
                                                {/* Drag handle visual */}
                                                <div className="flex flex-col items-start justify-start pt-2 text-slate-600 cursor-move shrink-0">
                                                    <span className="text-[10px] font-bold mb-1">#{index + 1}</span>
                                                    <GripVertical size={20} />
                                                </div>

                                                {/* 🚨 ALL LESSON CONTENT WRAPPER (This forces vertical stacking!) */}
                                                <div className="flex-1 flex flex-col min-w-0">
                                                    
                                                    {/* ROW 1: Basic Inputs */}
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div className="space-y-3">
                                                            <div>
                                                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Lesson Title</label>
                                                                <input 
                                                                    type="text" 
                                                                    className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-xs text-white focus:outline-none focus:border-blue-500"
                                                                    placeholder="e.g. Introduction to Support"
                                                                    value={lesson.title}
                                                                    onChange={(e) => handleLessonChange(lesson.id, 'title', e.target.value)}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Video Embed URL (Vimeo/YT)</label>
                                                                <input 
                                                                    type="text" 
                                                                    className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-xs text-white focus:outline-none focus:border-blue-500"
                                                                    placeholder="https://vimeo.com/..."
                                                                    value={lesson.videoUrl}
                                                                    onChange={(e) => handleLessonChange(lesson.id, 'videoUrl', e.target.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Lesson Text / HTML Content</label>
                                                            <textarea 
                                                                className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-xs text-white focus:outline-none focus:border-blue-500 h-[88px] resize-none font-mono"
                                                                placeholder="Lesson notes..."
                                                                value={lesson.content}
                                                                onChange={(e) => handleLessonChange(lesson.id, 'content', e.target.value)}
                                                            ></textarea>
                                                        </div>
                                                    </div>

                                                    {/* ROW 2: Lesson Resources */}
                                                    <div className="mt-4 pt-4 border-t border-slate-700">
                                                        <div className="flex justify-between items-center mb-3">
                                                            <h5 className="text-[10px] font-bold text-blue-400 uppercase tracking-wider flex items-center gap-1"><UploadCloud size={12}/> Lesson Resources</h5>
                                                        </div>
                                                        <div className="border border-dashed border-slate-600 rounded-lg p-4 text-center hover:border-blue-500/50 transition-colors relative bg-slate-900/50">
                                                            <input 
                                                                type="file" 
                                                                multiple 
                                                                onChange={(e) => handleLessonFileUpload(lesson.id, e)}
                                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                            />
                                                            <p className="text-[10px] font-bold text-white mb-0.5">Drag & Drop files here</p>
                                                            <p className="text-[9px] text-slate-500">or click to browse</p>
                                                        </div>
                                                        {(lesson.resources || []).length > 0 && (
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                                                                {lesson.resources.map(file => (
                                                                    <div key={file.id} className="flex items-center justify-between bg-slate-950 border border-slate-700 p-2 rounded">
                                                                        <div className="flex items-center gap-2 overflow-hidden">
                                                                            <FileText size={12} className="text-blue-400 shrink-0"/>
                                                                            <div className="truncate">
                                                                                <p className="text-[10px] font-bold text-white truncate">{file.name}</p>
                                                                                <p className="text-[8px] text-slate-500">{file.size}</p>
                                                                            </div>
                                                                        </div>
                                                                        <button onClick={() => handleRemoveLessonResource(lesson.id, file.id)} className="text-slate-500 hover:text-red-500 transition-colors p-1 shrink-0">
                                                                            <XCircle size={14}/>
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* ROW 3: Lesson Quiz */}
                                                    <div className="mt-4 pt-4 border-t border-slate-700">
                                                        <div className="flex justify-between items-center mb-3">
                                                            <h5 className="text-[10px] font-bold text-amber-500 uppercase tracking-wider flex items-center gap-1"><Award size={12}/> Lesson Quiz ({(lesson.quiz?.questions || []).length} Qs)</h5>
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex items-center gap-2">
                                                                    <label className="text-[10px] font-bold text-slate-400">Pass %:</label>
                                                                    <input 
                                                                        type="number" 
                                                                        className="w-12 bg-slate-900 border border-slate-600 rounded p-1 text-[10px] text-white text-center focus:outline-none focus:border-amber-500"
                                                                        value={(lesson.quiz || {}).passingScore || 80}
                                                                        onChange={(e) => handlePassingScore(lesson.id, e.target.value)}
                                                                    />
                                                                </div>
                                                                <button 
                                                                    onClick={() => handleAddQuestion(lesson.id)}
                                                                    className="bg-amber-600/20 hover:bg-amber-600 text-amber-500 hover:text-white border border-amber-500/30 text-[10px] font-bold px-2 py-1 rounded transition-colors flex items-center gap-1"
                                                                >
                                                                    <Plus size={12} /> Add Question
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {(lesson.quiz?.questions?.length > 0) && (
                                                            <div className="space-y-3">
                                                                {lesson.quiz.questions.map((q, qIndex) => (
                                                                    <div key={q.id} className="bg-slate-900 border border-slate-700 rounded-lg p-3 relative group">
                                                                        <button 
                                                                            onClick={() => handleRemoveQuestion(lesson.id, q.id)}
                                                                            className="absolute -top-2 -right-2 bg-slate-800 border border-slate-600 text-slate-500 hover:text-red-500 p-1 rounded-full transition-colors z-10 shadow-md"
                                                                        >
                                                                            <Trash2 size={10} />
                                                                        </button>

                                                                        <div className="flex justify-between items-center mb-2">
                                                                            <span className="text-[10px] font-bold text-blue-400">Q{qIndex + 1}</span>
                                                                            <select 
                                                                                value={q.type || 'multiple_choice'} 
                                                                                onChange={(e) => handleQuestionTypeChange(lesson.id, q.id, e.target.value)}
                                                                                className="bg-slate-950 border border-slate-700 rounded p-0.5 text-[10px] text-slate-300 focus:outline-none"
                                                                            >
                                                                                <option value="multiple_choice">Multiple Choice</option>
                                                                                <option value="true_false">True / False</option>
                                                                            </select>
                                                                        </div>

                                                                        <input 
                                                                            type="text" 
                                                                            className="w-full bg-slate-950 border border-slate-700 rounded p-2 text-xs text-white focus:outline-none focus:border-amber-500 mb-2"
                                                                            placeholder="Question text..."
                                                                            value={q.questionText}
                                                                            onChange={(e) => handleQuestionChange(lesson.id, q.id, e.target.value)}
                                                                        />

                                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                                            {q.options.map((opt, optIndex) => (
                                                                                <div key={optIndex} className={`flex items-center gap-2 p-1.5 rounded border transition-colors ${q.correctIndex === optIndex ? 'bg-emerald-900/20 border-emerald-500/50' : 'bg-slate-950 border-slate-700'}`}>
                                                                                    <input 
                                                                                        type="radio" 
                                                                                        name={`correct-${lesson.id}-${q.id}`} 
                                                                                        checked={q.correctIndex === optIndex}
                                                                                        onChange={() => handleCorrectAnswer(lesson.id, q.id, optIndex)}
                                                                                        className="w-3 h-3 text-emerald-500"
                                                                                    />
                                                                                    <input 
                                                                                        type="text" 
                                                                                        className={`w-full bg-transparent text-[10px] text-white focus:outline-none ${q.type === 'true_false' ? 'pointer-events-none text-slate-400 font-bold' : ''}`}
                                                                                        placeholder={`Option ${String.fromCharCode(65 + optIndex)}`}
                                                                                        value={opt}
                                                                                        onChange={(e) => handleOptionChange(lesson.id, q.id, optIndex, e.target.value)}
                                                                                        disabled={q.type === 'true_false'}
                                                                                    />
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>

                                                </div> {/* END OF ALL LESSON CONTENT WRAPPER */}

                                                {/* Delete Lesson Button */}
                                                <button 
                                                    onClick={() => handleRemoveLesson(lesson.id)}
                                                    className="absolute -top-3 -right-3 bg-slate-900 border border-slate-700 text-slate-500 hover:text-red-500 hover:border-red-500 p-1.5 rounded-full transition-colors shadow-lg"
                                                    title="Remove Lesson"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* SECTION 3: Downloadable Resources */}
                            <div className="space-y-4 bg-slate-950/50 p-6 rounded-xl border border-slate-800 mt-8">
                                <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2 border-b border-slate-800 pb-3">
                                    3. Course Resources (PDFs/Files)
                                </h4>
                                
                                <div className="border-2 border-dashed border-slate-700 rounded-xl p-8 text-center hover:border-amber-500/50 transition-colors relative bg-slate-900/50">
                                    <input 
                                        type="file" 
                                        multiple 
                                        onChange={handleFileUpload}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    />
                                    <UploadCloud size={32} className="mx-auto text-slate-500 mb-3" />
                                    <p className="text-sm font-bold text-white mb-1">Drag & Drop files here</p>
                                    <p className="text-xs text-slate-500">or click to browse (PDF, DOCX, XLSX)</p>
                                </div>

                                {(formData.resources || []).length > 0 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                                        {formData.resources.map(file => (
                                            <div key={file.id} className="flex items-center justify-between bg-slate-900 border border-slate-700 p-3 rounded-lg">
                                                <div className="flex items-center gap-3 overflow-hidden">
                                                    <FileText size={16} className="text-blue-400 shrink-0"/>
                                                    <div className="truncate">
                                                        <p className="text-xs font-bold text-white truncate">{file.name}</p>
                                                        <p className="text-[10px] text-slate-500">{file.size}</p>
                                                    </div>
                                                </div>
                                                <button onClick={() => handleRemoveResource(file.id)} className="text-slate-500 hover:text-red-500 transition-colors p-1">
                                                    <XCircle size={16}/>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div> {/* 🚨 THIS IS THE MISSING DIV! 🚨 */}

                        {/* Modal Footer (Fixed at bottom) */}
                        <div className="p-6 border-t border-slate-800 bg-slate-900 rounded-b-2xl flex justify-between items-center shrink-0">
                            
                            {/* Publish Toggle */}
                            <div className="flex items-center gap-3">
                                <button 
                                    onClick={() => setFormData({...formData, status: formData.status === 'Published' ? 'Draft' : 'Published'})}
                                    className={`w-12 h-6 rounded-full p-1 transition-colors relative flex items-center ${formData.status === 'Published' ? 'bg-emerald-500' : 'bg-slate-700'}`}
                                >
                                    <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform absolute ${formData.status === 'Published' ? 'translate-x-6' : 'translate-x-0'}`} />
                                </button>
                                <div>
                                    <span className={`text-sm font-bold ${formData.status === 'Published' ? 'text-emerald-400' : 'text-slate-500'}`}>
                                        {formData.status === 'Published' ? 'Course is LIVE' : 'Saved as Draft'}
                                    </span>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <button 
                                    onClick={handleCloseModal}
                                    className="px-6 py-2.5 rounded-lg text-sm font-bold text-slate-300 hover:bg-slate-800 transition-colors border border-transparent hover:border-slate-700"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={handleSaveCourse}
                                    className="px-8 py-2.5 rounded-lg text-sm font-bold text-white bg-amber-600 hover:bg-amber-500 transition-colors shadow-lg shadow-amber-900/20 flex items-center gap-2"
                                >
                                    <Save size={16} /> Save Course
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- HUB BUILDER MODAL --- */}
            {isHubModalOpen && (
                <div className="fixed inset-0 z-[80] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-950/50">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2"><Users className="text-blue-500"/> Configure Hub</h2>
                            <button onClick={() => setIsHubModalOpen(false)} className="text-slate-500 hover:text-white transition-colors"><X size={24}/></button>
                        </div>
                        <div className="p-6 space-y-6">
                            <div>
                                <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Hub Name</label>
                                <input value={hubFormData.name} onChange={(e) => setHubFormData({...hubFormData, name: e.target.value})} className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3 text-white focus:border-blue-500 outline-none" placeholder="e.g. Alpha Trading Club"/>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Assign Hub Leaders (Partners)</label>
                                
                                {/* 🚨 NEW: Display Selected Leaders as Badges/Pills */}
                                <div className="flex flex-wrap gap-2 mb-3">
                                   {/* 🚨 FAIL-SAFE: Ensure we are always mapping over an array, even if it's empty */}
                                        {(hubFormData.lead_user_ids || []).map(id => {
                                        const partner = availablePartners.find(p => p.id === id);
                                        return (
                                            <div key={id} className="bg-blue-600/20 border border-blue-500/30 text-blue-400 text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-2">
                                                {partner?.name || 'Unknown User'}
                                                <button onClick={() => setHubFormData(prev => ({...prev, lead_user_ids: (prev.lead_user_ids || []).filter(uid => uid !== id)}))} className="hover:text-red-400 transition-colors">
                                                <X size={12}/>
                                                </button>
                                            </div>
                                        )
                                    })}
                                    {hubFormData.lead_user_ids.length === 0 && <span className="text-xs text-slate-500 italic">No leaders assigned yet...</span>}
                                </div>

                                {/* Dropdown to Add More Leaders */}
                                <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden focus-within:border-blue-500 transition-colors">
                                    <div className="flex items-center px-3 border-b border-slate-700 bg-slate-900/50">
                                        <Search size={14} className="text-slate-500" />
                                        <input 
                                            type="text" 
                                            placeholder="Search to add a partner..." 
                                            value={partnerSearchTerm}
                                            onChange={(e) => setPartnerSearchTerm(e.target.value)}
                                            className="w-full bg-transparent p-2.5 text-sm text-white focus:outline-none"
                                        />
                                    </div>
                                    <select 
                                        value="" 
                                        onChange={(e) => {
                                            const selectedId = e.target.value;
                                            if (selectedId) {
                                                // 🚨 FUNCTIONAL UPDATE: Guarantees we never overwrite existing pills
                                                setHubFormData(prev => {
                                                    const currentLeads = prev.lead_user_ids || [];
                                                    if (!currentLeads.includes(selectedId)) {
                                                        return { ...prev, lead_user_ids: [...currentLeads, selectedId] };
                                                    }
                                                    return prev;
                                                });
                                            }
                                            setPartnerSearchTerm(''); 
                                        }} 
                                        className="w-full bg-slate-800 p-3 text-sm text-white focus:outline-none cursor-pointer"
                                    >
                                        <option value="">-- Click to add a Partner --</option>
                                        {availablePartners
                                            .filter(p => !(hubFormData.lead_user_ids || []).includes(p.id)) // 🚨 Filter safety
                                            .filter(p => 
                                                (p.name || '').toLowerCase().includes(partnerSearchTerm.toLowerCase()) || 
                                                (p.email || '').toLowerCase().includes(partnerSearchTerm.toLowerCase()) ||
                                                (p.id || '').toLowerCase().includes(partnerSearchTerm.toLowerCase()) // 🚨 Now searches by ID too
                                            )
                                            .map(p => <option key={p.id} value={p.id}>{p.name || 'Unnamed Client'} ({p.email || p.id})</option>) // 🚨 Shows ID if email is missing
                                        }
                                    </select>
                                </div>
                            </div>
                            
                            <div className="pt-2 pb-4 border-b border-slate-800">
                                <label className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-2 block">Exclusive Copy Trading IDs (Comma Separated)</label>
                                <input value={hubFormData.copy_whitelist} onChange={(e) => setHubFormData({...hubFormData, copy_whitelist: e.target.value})} className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-white focus:border-blue-500 outline-none mb-3" placeholder="e.g. COPY-1001, COPY-9923"/>
                                
                                <label className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-2 block">Exclusive PAMM IDs (Comma Separated)</label>
                                <input value={hubFormData.pamm_whitelist} onChange={(e) => setHubFormData({...hubFormData, pamm_whitelist: e.target.value})} className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-white focus:border-blue-500 outline-none" placeholder="e.g. PAMM-500, PAMM-771"/>
                            </div>

                            <div className="space-y-3">
                                <label className="text-xs font-bold text-slate-400 uppercase block">Permissions & Network Targeting</label>
                                
                                {/* 🚨 NEW: DEFILY NETWORK AUTO-ROUTER */}
                                <label className="flex items-center justify-between p-3 bg-blue-900/20 rounded-xl border border-blue-500/30 cursor-pointer shadow-inner">
                                    <div>
                                        <span className="text-sm font-bold text-blue-400 block">Target DeFily Network</span>
                                        <span className="text-[10px] text-blue-300/70">Auto-applies whitelists to ALL users with numeric referral codes.</span>
                                    </div>
                                    <input 
                                        type="checkbox" 
                                        checked={hubFormData.permissions.target_defily_network || false} 
                                        onChange={(e) => setHubFormData({...hubFormData, permissions: {...hubFormData.permissions, target_defily_network: e.target.checked}})} 
                                        className="w-4 h-4 accent-blue-500 rounded"
                                    />
                                </label>

                                {Object.keys(hubFormData.permissions).filter(k => k !== 'target_defily_network').map(key => (
                                    <label key={key} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl border border-slate-700 cursor-pointer">
                                        <span className="text-sm font-bold text-white capitalize">{key.replace(/_/g, ' ')}</span>
                                        <input type="checkbox" checked={hubFormData.permissions[key]} onChange={(e) => setHubFormData({...hubFormData, permissions: {...hubFormData.permissions, [key]: e.target.checked}})} className="w-4 h-4 accent-blue-500 rounded"/>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="p-6 border-t border-slate-800 bg-slate-950/50 flex justify-end gap-3">
                            <button onClick={() => setIsHubModalOpen(false)} className="px-6 py-2.5 rounded-xl font-bold text-slate-300 bg-slate-800 hover:bg-slate-700 transition-colors">Cancel</button>
                            <button onClick={handleSaveHub} className="px-6 py-2.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-500 transition-colors flex items-center gap-2"><Save size={16}/> Save Hub</button>
                        </div>
                    </div>
                </div>
          )}
        </div>
    );
};

export default AcademyAdmin;